(function () {
  'use strict';

  var reducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var registryCache = null;
  var registryPromise = null;
  var registryUrlCache = null;
  var started = false;
  var registryApplied = false;
  var sharedObserver = null;
  var activeStoryVideo = null;
  var pendingVideoQueue = [];
  var mediaWriteScheduled = false;
  var pendingMediaWrites = [];

  function scheduleAfterPaint(task) {
    var run = function () {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(task, { timeout: 1500 });
        return;
      }
      window.setTimeout(task, 0);
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(run);
      });
      return;
    }

    window.setTimeout(run, 0);
  }

  function registrySource() {
    if (registryUrlCache !== null) return registryUrlCache;

    var node = document.querySelector('[data-story-media-registry-src]');
    if (!node) {
      registryUrlCache = '';
      return registryUrlCache;
    }

    try {
      registryUrlCache = JSON.parse(node.textContent);
    } catch (error) {
      registryUrlCache = '';
    }

    return registryUrlCache;
  }

  function registryAssetUrl(filename) {
    var src = registrySource();
    if (!src || !filename) return '';

    try {
      var url = new URL(src, window.location.href);
      var path = url.pathname.split('/');
      path[path.length - 1] = filename;
      url.pathname = path.join('/');
      url.search = '';
      return url.toString();
    } catch (error) {
      return '';
    }
  }

  function flushMediaWrites() {
    mediaWriteScheduled = false;
    var writes = pendingMediaWrites;
    pendingMediaWrites = [];
    writes.forEach(function (write) {
      write();
    });
  }

  function queueMediaWrite(write) {
    pendingMediaWrites.push(write);
    if (mediaWriteScheduled) return;
    mediaWriteScheduled = true;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(flushMediaWrites);
    } else {
      window.setTimeout(flushMediaWrites, 0);
    }
  }

  function setMediaState(slot, state, message) {
    queueMediaWrite(function () {
      var status = slot.querySelector('[data-story-media-status]');
      var retry = slot.querySelector('[data-story-media-retry]');

      if (slot.getAttribute('data-media-state') !== state) {
        slot.setAttribute('data-media-state', state);
      }

      slot.classList.toggle('story-media--loaded', state === 'ready');
      slot.classList.toggle('story-media--error', state === 'error');
      slot.classList.toggle('story-media--pending', state !== 'ready');

      if (status) {
        status.textContent = message || '';
        status.hidden = state === 'ready';
      }

      if (retry) retry.hidden = state !== 'error';
    });
  }

  function isAudioEssentialVideo(video) {
    if (!video) return false;
    if (video.hasAttribute('data-story-media-audio-essential')) return true;
    var slot = video.closest('[data-story-media]');
    if (!slot) return false;
    var key = slot.getAttribute('data-media-key') || '';
    return key === 'services.activations.bridget' ||
      key === 'services.activations.testimonials';
  }

  function unlockAudioEssentialVideo(video) {
    if (!isAudioEssentialVideo(video)) return;
    video.defaultMuted = false;
    video.muted = false;
    video.removeAttribute('muted');
    if (video.volume === 0) video.volume = 1;
  }

  function bindAudioEssentialEvents(video) {
    if (!isAudioEssentialVideo(video)) return;
    if (video.getAttribute('data-story-audio-events') === 'bound') return;
    video.setAttribute('data-story-audio-events', 'bound');

    var unlock = function () {
      unlockAudioEssentialVideo(video);
    };

    video.addEventListener('play', unlock);
    video.addEventListener('playing', unlock);
    unlockAudioEssentialVideo(video);
  }

  function safeAutoplay(video, slot) {
    if (isAudioEssentialVideo(video)) return;
    if (!video.autoplay || reducedMotion) return;

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        slot.setAttribute('data-media-autoplay', 'blocked');
        setMediaState(slot, 'ready');
      });
    }
  }

  function bindVideoEvents(video, slot) {
    if (video.getAttribute('data-story-media-events') === 'bound') return;
    video.setAttribute('data-story-media-events', 'bound');
    bindAudioEssentialEvents(video);

    video.addEventListener('loadedmetadata', function () {
      setMediaState(slot, 'ready');
    });
    video.addEventListener('canplay', function () {
      setMediaState(slot, 'ready');
      safeAutoplay(video, slot);
    });
    video.addEventListener('error', function () {
      setMediaState(slot, 'error', 'Video unavailable. Retry when ready.');
      if (activeStoryVideo === video) {
        activeStoryVideo = null;
        drainVideoQueue();
      }
    });
  }

  function bindImageEvents(image, slot) {
    if (image.getAttribute('data-story-media-events') === 'bound') return;
    image.setAttribute('data-story-media-events', 'bound');

    image.addEventListener('load', function () {
      setMediaState(slot, 'ready');
    });
    image.addEventListener('error', function () {
      setMediaState(slot, 'error', 'Image unavailable. Retry when ready.');
    });

    if (image.complete && image.naturalWidth > 0) {
      setMediaState(slot, 'ready');
    }
  }

  function ensureSharedObserver() {
    if (sharedObserver || !('IntersectionObserver' in window)) return sharedObserver;

    sharedObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var target = entry.target;
        var kind = target.getAttribute('data-bbjt-observe');

        if (kind === 'story-count') {
          if (!entry.isIntersecting) return;
          animateCount(target);
          sharedObserver.unobserve(target);
          return;
        }

        if (kind !== 'story-video') return;

        var slot = target.closest('[data-story-media]');
        if (!slot) return;

        if (entry.isIntersecting) {
          queueVideoActivation(slot, target, false);
        } else if (entry.intersectionRatio === 0) {
          deactivateFarVideo(slot, target);
        }
      });
      }, {
      rootMargin: '32px 0px',
      threshold: [0, 0.01]
    });

    return sharedObserver;
  }

  function drainVideoQueue() {
    if (activeStoryVideo) return;
    while (pendingVideoQueue.length) {
      var next = pendingVideoQueue.shift();
      if (!next || !next.video.isConnected) continue;
      if (next.video.getAttribute('data-story-media-near') !== 'true') continue;
      activateVideoSource(next.slot, next.video, next.forceRetry);
      return;
    }
  }

  function queueVideoActivation(slot, video, forceRetry) {
    video.setAttribute('data-story-media-near', 'true');

    // Money Lab interview/testimonial videos keep native sources and must not
    // wait behind decorative story-video concurrency.
    if (isAudioEssentialVideo(video)) {
      activateVideoSource(slot, video, forceRetry);
      return;
    }

    if (video.getAttribute('data-story-media-activated') === 'true' && !forceRetry) {
      if (video.readyState >= 1) setMediaState(slot, 'ready');
      return;
    }

    if (activeStoryVideo && activeStoryVideo !== video) {
      var alreadyQueued = pendingVideoQueue.some(function (item) {
        return item.video === video;
      });
      if (!alreadyQueued) {
        pendingVideoQueue.push({ slot: slot, video: video, forceRetry: forceRetry });
      }
      return;
    }

    activateVideoSource(slot, video, forceRetry);
  }

  function deactivateFarVideo(slot, video) {
    video.setAttribute('data-story-media-near', 'false');

    if (video.getAttribute('data-story-media-activated') !== 'true') return;

    // Keep Money Lab interview sources attached so Play always works.
    if (isAudioEssentialVideo(video)) {
      try {
        if (!video.paused) video.pause();
      } catch (error) {}
      setMediaState(slot, 'ready');
      return;
    }

    try {
      if (!video.paused) video.pause();
    } catch (error) {}

    var src = video.getAttribute('data-story-media-src') || video.getAttribute('src') || '';
    if (src) {
      video.setAttribute('data-story-media-src-pending', src);
      video.removeAttribute('src');
      video.removeAttribute('data-story-media-src');
      video.preload = 'none';
    }

    video.setAttribute('data-story-media-activated', 'false');
    setMediaState(slot, 'ready');

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(function () {
        try { video.load(); } catch (error) {}
      }, { timeout: 2500 });
    } else {
      window.setTimeout(function () {
        try { video.load(); } catch (error) {}
      }, 250);
    }

    if (activeStoryVideo === video) {
      activeStoryVideo = null;
      drainVideoQueue();
    }

    pendingVideoQueue = pendingVideoQueue.filter(function (item) {
      return item.video !== video;
    });
  }

  function activateVideoSource(slot, video, forceRetry) {
    if (!video) return;

    var pending = video.getAttribute('data-story-media-src-pending');
    var current = video.getAttribute('data-story-media-src');
    var inlineSrc = video.getAttribute('src') || '';
    var url = pending || current || inlineSrc;
    if (!url) return;

    bindVideoEvents(video, slot);
    unlockAudioEssentialVideo(video);

    if (video.getAttribute('data-story-media-activated') === 'true' && !forceRetry) {
      if (video.readyState >= 1) setMediaState(slot, 'ready');
      if (!isAudioEssentialVideo(video)) activeStoryVideo = video;
      if (isAudioEssentialVideo(video) && video.getAttribute('src') !== url) {
        video.src = url;
      }
      return;
    }

    if (!isAudioEssentialVideo(video)) {
      activeStoryVideo = video;
    }
    video.setAttribute('data-story-media-activated', 'true');
    video.setAttribute('data-story-media-src', url);
    video.removeAttribute('data-story-media-src-pending');
    video.preload = 'metadata';
    if (video.getAttribute('src') !== url) {
      video.src = url;
    }

    try {
      video.load();
    } catch (error) {}

    setMediaState(slot, 'ready');
  }

  function observeVideoActivation(slot, video) {
    if (!video || video.getAttribute('data-story-media-observed') === 'true') return;
    video.setAttribute('data-story-media-observed', 'true');
    video.setAttribute('data-bbjt-observe', 'story-video');

    var observer = ensureSharedObserver();
    if (!observer) {
      queueVideoActivation(slot, video, false);
      return;
    }

    observer.observe(video);
  }

  function enhanceExistingImage(slot, image, item) {
    if (item && item.alt) image.alt = item.alt;
    bindImageEvents(image, slot);
    if (image.complete && image.naturalWidth > 0) {
      setMediaState(slot, 'ready');
    }
  }

  function enhanceExistingVideo(slot, video, item) {
    if (video.getAttribute('data-story-media-enhanced') === 'true' && item) {
      if (isAudioEssentialVideo(video)) {
        unlockAudioEssentialVideo(video);
        bindAudioEssentialEvents(video);
      }
      observeVideoActivation(slot, video);
      return;
    }

    var audioEssential = isAudioEssentialVideo(video);

    video.controls = !(item && item.controls === false);
    video.playsInline = true;
    video.setAttribute('webkit-playsinline', '');
    video.autoplay = !!(item && item.autoplay === true && !reducedMotion && !audioEssential);
    if (audioEssential) {
      unlockAudioEssentialVideo(video);
    } else if (item && item.muted === false) {
      video.defaultMuted = false;
      video.muted = false;
      video.removeAttribute('muted');
    } else {
      video.muted = item && item.muted !== undefined ? item.muted !== false : video.muted;
    }
    video.loop = !!(item && item.loop === true);
    video.preload = audioEssential ? 'metadata' : 'none';
    video.setAttribute(
      'aria-label',
      (item && (item.alt || item.caption)) || video.getAttribute('aria-label') || 'Beats By JT video'
    );
    video.setAttribute('data-story-media-enhanced', 'true');
    if (audioEssential) {
      video.setAttribute('data-story-media-audio-essential', '');
      bindAudioEssentialEvents(video);
    }

    if (item) {
      var posterFilename = item.poster || item.posterAssetFilename || '';
      var posterUrl = posterFilename ? registryAssetUrl(posterFilename) : '';
      if (posterUrl && video.poster !== posterUrl) video.poster = posterUrl;

      var sourceFilename = item.source || item.assetFilename;
      var sourceUrl = sourceFilename ? registryAssetUrl(sourceFilename) : '';
      if (sourceUrl) {
        var activeSrc = video.getAttribute('data-story-media-src') || video.getAttribute('src') || '';
        if (audioEssential) {
          video.setAttribute('data-story-media-src', sourceUrl);
          video.removeAttribute('data-story-media-src-pending');
          if (activeSrc !== sourceUrl) {
            video.src = sourceUrl;
          }
        } else if (activeSrc !== sourceUrl) {
          video.setAttribute('data-story-media-src-pending', sourceUrl);
        }
      }
    }

    setMediaState(slot, 'ready');
    observeVideoActivation(slot, video);
  }

  function ensureVideo(slot, frame, item, url, forceRetry) {
    var video = frame.querySelector('video[data-story-media-node]');
    if (!video) {
      video = document.createElement('video');
      video.className = 'story-media__video';
      video.setAttribute('data-story-media-node', '');
      frame.appendChild(video);
    }

    video.setAttribute('data-story-media-src-pending', url);
    enhanceExistingVideo(slot, video, item);
    if (forceRetry) queueVideoActivation(slot, video, true);
  }

  function ensureImage(slot, frame, item, url, forceRetry) {
    var image = frame.querySelector('img[data-story-media-node]');
    if (!image) {
      image = document.createElement('img');
      image.className = 'story-media__image';
      image.setAttribute('data-story-media-node', '');
      image.alt = (item && item.alt) || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      frame.appendChild(image);
    }

    if (image.getAttribute('data-story-media-src') !== url || forceRetry) {
      image.setAttribute('data-story-media-src', url);
      image.src = url;
    }

    enhanceExistingImage(slot, image, item);
  }

  function enhanceExistingNode(slot, item) {
    var frame = slot.querySelector('.story-media__frame');
    if (!frame) return;

    var node = frame.querySelector('[data-story-media-node]');
    if (!node) return;

    if (node.tagName === 'VIDEO') {
      enhanceExistingVideo(slot, node, item);
      return;
    }

    if (node.tagName === 'IMG') {
      enhanceExistingImage(slot, node, item);
    }
  }

  function hydrateMediaSlot(slot, item, forceRetry) {
    var frame = slot.querySelector('.story-media__frame');
    var existing = frame && frame.querySelector('[data-story-media-node]');
    if (existing && !forceRetry) {
      enhanceExistingNode(slot, item);
      return;
    }

    var sourceFilename = item && (item.source || item.assetFilename);
    if (!frame || !item || !sourceFilename) {
      if (!existing) setMediaState(slot, 'error', 'Media unavailable.');
      return;
    }

    var url = registryAssetUrl(sourceFilename);
    if (!url) {
      if (!existing) setMediaState(slot, 'error', 'Media unavailable.');
      return;
    }

    var type = item.type || slot.getAttribute('data-media-kind') || 'image';
    slot.setAttribute('data-media-kind', type);

    if (type === 'video') {
      ensureVideo(slot, frame, item, url, forceRetry);
      return;
    }

    ensureImage(slot, frame, item, url, forceRetry);
  }

  function bindRetry(slot) {
    var retry = slot.querySelector('[data-story-media-retry]');
    if (!retry || retry.getAttribute('data-story-media-events') === 'bound') return;
    retry.setAttribute('data-story-media-events', 'bound');

    retry.addEventListener('click', function () {
      var key = slot.getAttribute('data-media-key');
      var item = registryCache && registryCache.items ? registryCache.items[key] : null;
      if (item) {
        hydrateMediaSlot(slot, item, true);
        return;
      }

      loadMediaRegistry().then(function (registry) {
        var retryItem = registry && registry.items ? registry.items[key] : null;
        hydrateMediaSlot(slot, retryItem, true);
      });
    });
  }

  function applyMediaRegistry(registry) {
    if (!registry || !registry.items) return;
    registryCache = registry;

    if (registryApplied) {
      Array.prototype.slice.call(document.querySelectorAll('[data-story-media]')).forEach(function (slot) {
        var key = slot.getAttribute('data-media-key');
        var item = registry.items[key];
        if (item) hydrateMediaSlot(slot, item, false);
      });
      return;
    }

    registryApplied = true;

    var slots = Array.prototype.slice.call(document.querySelectorAll('[data-story-media]'));
    slots.forEach(function (slot) {
      var key = slot.getAttribute('data-media-key');
      var item = registry.items[key];
      var caption = slot.querySelector('[data-story-media-caption]');
      var fallbackCaption = slot.getAttribute('data-media-fallback-label') || 'Visual source reserved';
      var existing = slot.querySelector('[data-story-media-node]');

      bindRetry(slot);

      if (!item) {
        if (!existing) {
          if (caption) caption.textContent = fallbackCaption;
          setMediaState(slot, 'error', 'Media unavailable.');
        }
        return;
      }

      slot.setAttribute('data-media-category', item.category || '');
      slot.setAttribute('data-media-required-file', item.filenameNeeded || '');
      if (item.fit) slot.setAttribute('data-media-fit', item.fit);
      if (item.orientation) slot.setAttribute('data-media-orientation', item.orientation);
      if (item.aspectRatio) {
        slot.setAttribute('data-media-aspect-ratio', item.aspectRatio);
        slot.style.setProperty('--story-media-aspect-ratio', item.aspectRatio);
      }

      if (caption && item.caption) caption.textContent = item.caption;
      hydrateMediaSlot(slot, item, false);
    });
  }

  function loadMediaRegistry() {
    if (registryCache) {
      applyMediaRegistry(registryCache);
      return Promise.resolve(registryCache);
    }
    if (registryPromise) return registryPromise;

    var src = registrySource();
    if (!src || !window.fetch) return Promise.resolve(null);

    registryPromise = window.fetch(src, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Media registry unavailable');
        return response.json();
      })
      .then(function (registry) {
        applyMediaRegistry(registry);
        return registry;
      })
      .catch(function () {
        registryPromise = null;
        document.documentElement.setAttribute('data-story-media-registry', 'unavailable');
        Array.prototype.slice.call(document.querySelectorAll('[data-story-media]')).forEach(function (slot) {
          if (!slot.querySelector('[data-story-media-node]')) {
            setMediaState(slot, 'error', 'Media temporarily unavailable.');
          }
          bindRetry(slot);
        });
        return null;
      });

    return registryPromise;
  }

  function countTarget(item) {
    var raw = item.getAttribute('data-count-target') || '';
    var numeric = Number(raw.replace(/,/g, ''));
    return Number.isFinite(numeric) ? numeric : null;
  }

  function setCountValue(item, value) {
    var prefix = item.getAttribute('data-count-prefix') || '';
    var suffix = item.getAttribute('data-count-suffix') || '';
    var rounded = Math.round(value);
    item.textContent = prefix + rounded.toLocaleString() + suffix;
  }

  function animateCount(item) {
    if (item.getAttribute('data-count-complete') === 'true') return;

    var target = countTarget(item);
    if (target === null) return;

    item.setAttribute('data-count-complete', 'true');

    if (reducedMotion || target === 0) {
      setCountValue(item, target);
      return;
    }

    var duration = 950;
    var startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      setCountValue(item, target * eased);
      if (progress < 1) window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  function revealStoryItems() {
    var counters = Array.prototype.slice.call(document.querySelectorAll('[data-story-count]')).filter(function (counter) {
      return counter.getAttribute('data-story-count-observed') !== 'true';
    });
    if (!counters.length) return;

    document.body.classList.add('story-motion-ready');

    var observer = ensureSharedObserver();
    if (!observer) {
      counters.forEach(animateCount);
      return;
    }

    counters.forEach(function (counter) {
      counter.setAttribute('data-story-count-observed', 'true');
      counter.setAttribute('data-bbjt-observe', 'story-count');
      observer.observe(counter);
    });
  }

  function prepareServerRenderedMedia() {
    Array.prototype.slice.call(document.querySelectorAll('[data-story-media]')).forEach(function (slot) {
      if (slot.getAttribute('data-story-media-prepared') === 'true') {
        var existingVideo = slot.querySelector('video[data-story-media-node]');
        if (existingVideo) observeVideoActivation(slot, existingVideo);
        return;
      }

      slot.setAttribute('data-story-media-prepared', 'true');
      var image = slot.querySelector('img[data-story-media-node]');
      var video = slot.querySelector('video[data-story-media-node]');
      bindRetry(slot);

      if (image) {
        bindImageEvents(image, slot);
        setMediaState(slot, 'ready');
      }

      if (video) {
        if (isAudioEssentialVideo(video)) {
          video.preload = 'metadata';
          unlockAudioEssentialVideo(video);
          bindAudioEssentialEvents(video);
          var readySrc = video.getAttribute('src') ||
            video.getAttribute('data-story-media-src') ||
            video.getAttribute('data-story-media-src-pending') ||
            '';
          if (readySrc) {
            video.setAttribute('data-story-media-src', readySrc);
            video.removeAttribute('data-story-media-src-pending');
            if (video.getAttribute('src') !== readySrc) video.src = readySrc;
            video.setAttribute('data-story-media-activated', 'true');
          }
        } else {
          video.preload = 'none';
        }
        setMediaState(slot, 'ready');
        observeVideoActivation(slot, video);
      }
    });
  }

  function refreshVideoState() {
    Array.prototype.slice.call(
      document.querySelectorAll('video[data-story-media-node][data-story-media-activated="true"]')
    ).forEach(function (video) {
      var slot = video.closest('[data-story-media]');
      if (!slot) return;

      if (video.readyState >= 1) {
        setMediaState(slot, 'ready');
      } else if (video.networkState === 3) {
        setMediaState(slot, 'error', 'Video unavailable. Retry when ready.');
      }
    });
  }

  function initStory(options) {
    var settings = options || {};

    if (settings.force) {
      prepareServerRenderedMedia();
      scheduleAfterPaint(function () {
        revealStoryItems();
        loadMediaRegistry();
        refreshVideoState();
      });
      return;
    }

    if (started) return;
    started = true;

    prepareServerRenderedMedia();
    scheduleAfterPaint(function () {
      revealStoryItems();
      loadMediaRegistry();
    });
  }

  window.BBJTStoryMedia = {
    initialize: function () { initStory({ force: true }); },
    refreshVideos: refreshVideoState
  };

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      started = false;
      initStory({ force: true });
    }
  });

  document.addEventListener('shopify:section:load', function () {
    registryApplied = false;
    started = false;
    initStory({ force: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initStory();
    }, { once: true });
  } else {
    initStory();
  }
})();
